import React, { useEffect, useState } from "react";
import logger from "../lib/logger";
import { Eyebrow } from "../components/Primitives";
import { api } from "../lib/api";
import MembershipReceipt from "./MembershipReceipt";

const fmtCredit = (cents, currency = "usd") =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format((cents || 0) / 100);

export default function Membership({ user, onBack }) {
    const [showTerms, setShowTerms] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);
    const [receipt, setReceipt] = useState(null);
    const number = user?.founding_member_number;
    const joined = user?.founding_member_at ? new Date(user.founding_member_at).toLocaleDateString() : "—";
    const credit = receipt ? fmtCredit(receipt.amount, receipt.currency) : "$99.00";

    useEffect(() => {
        if (!user?.session_id || !number) return;
        api.getReceipt(user.session_id)
            .then(setReceipt)
            .catch(err => logger.warn("receipt fetch failed:", err));
    }, [user?.session_id, number]);

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="membership-screen">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="membership-back-btn" onClick={onBack} className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Settings</span>
            </div>

            <div className="px-7 pt-4 pb-2">
                <Eyebrow>Your account</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-[1.1]">
                    <span className="italic">Your</span> Founding Membership
                </h1>
            </div>

            <div className="px-6 py-5 space-y-2.5">
                <Row label="Status" value={number ? `Founding Member #${number}` : "—"} testid="row-status" />
                <Row label="Joined" value={joined} testid="row-joined" />
                <Row label="Credit on file" value={credit} testid="row-credit" />
                <Row label="Credit expires" value="Never" testid="row-expires" />
            </div>

            <div className="px-7 pb-2">
                <p className="text-sm text-ink leading-relaxed">
                    Your $99 credit will be applied automatically to any subscription tier you choose when Ever After officially launches. You'll receive advance notice before launch with full pricing details. No subscription is active or pending until you opt in.
                </p>
            </div>

            <div className="px-6 py-6 space-y-2">
                <ActionRow data-testid="ms-view-receipt" label="View receipt">
                    <button
                        data-testid="ms-view-receipt-btn"
                        onClick={() => setShowReceipt(true)}
                        disabled={!receipt}
                        className="text-[10px] tracking-[0.28em] uppercase text-burgundy disabled:opacity-50"
                    >
                        {receipt ? "Open" : "Loading…"}
                    </button>
                </ActionRow>
                <ActionRow data-testid="ms-review-terms" label="Review founding terms">
                    <button
                        onClick={() => setShowTerms(true)}
                        className="text-[10px] tracking-[0.28em] uppercase text-burgundy"
                    >
                        Read
                    </button>
                </ActionRow>
                <ActionRow data-testid="ms-privacy-data" label="Privacy & data">
                    <a
                        href="/settings"
                        className="text-[10px] tracking-[0.28em] uppercase text-burgundy"
                    >
                        Open
                    </a>
                </ActionRow>
                <ActionRow data-testid="ms-contact-support" label="Contact support">
                    <a
                        href="mailto:info@youreverafter.com"
                        className="text-[10px] tracking-[0.28em] uppercase text-burgundy"
                    >
                        Email
                    </a>
                </ActionRow>
            </div>

            {showReceipt && receipt && (
                <MembershipReceipt receipt={receipt} onClose={() => setShowReceipt(false)} />
            )}

            {showTerms && (
                <div
                    className="fixed inset-0 bg-black/50 z-[70] flex items-end sm:items-center justify-center px-3 pb-3 sm:p-6"
                    onClick={() => setShowTerms(false)}
                    data-testid="membership-terms-modal"
                >
                    <div onClick={e => e.stopPropagation()} className="w-full sm:max-w-md bg-cream border border-burgundy/20 max-h-[85vh] flex flex-col">
                        <div className="px-6 pt-6 pb-3 border-b border-burgundy/15">
                            <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Founding Member</div>
                            <h3 className="mt-1 font-display text-2xl text-burgundy"><span className="italic">Your</span> terms</h3>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-4 text-sm leading-relaxed text-ink space-y-3">
                            <p>By purchasing Ever After Founding Membership for $99, you agreed to the following:</p>
                            <ul className="list-disc pl-5 space-y-2">
                                <li>Immediate access to your full compatibility assessment, archetype reveal, and all currently available beta features.</li>
                                <li>Your $99 founding fee will be applied as a $99 credit toward any subscription tier you select when Ever After officially launches.</li>
                                <li>Your $99 credit does not expire and will be honored at launch regardless of when launch occurs.</li>
                                <li>Your founding access during the beta period does not auto-renew and will not result in additional charges.</li>
                                <li>You may request a full refund within 30 days of purchase for any reason by emailing <a href="mailto:info@youreverafter.com" className="underline text-burgundy">info@youreverafter.com</a>.</li>
                                <li>Founding Membership status is non-transferable.</li>
                                <li>Ever After reserves the right to modify features, pricing, and product offerings during the beta period. Your $99 credit remains honored regardless of changes.</li>
                            </ul>
                        </div>
                        <div className="px-6 py-4 border-t border-burgundy/15">
                            <button onClick={() => setShowTerms(false)} className="w-full bg-burgundy text-cream uppercase text-[11px] tracking-[0.22em] py-3">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ label, value, testid }) {
    return (
        <div className="flex items-center justify-between border-b border-burgundy/10 px-1 py-3" data-testid={testid}>
            <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">{label}</span>
            <span className="font-display text-lg text-burgundy">{value}</span>
        </div>
    );
}

function ActionRow({ label, children, ...rest }) {
    return (
        <div className="flex items-center justify-between border border-burgundy/15 bg-cream px-4 py-3" {...rest}>
            <span className="font-display text-base text-ink">{label}</span>
            {children}
        </div>
    );
}
